import type { CAHeadingColor, CAHeadingLevel } from '@ds/interfaces'
import { DELAY } from '@vuejs/helpers/_constants'
import { defineComponent, type PropType } from 'vue'

import CAIconComponent from '../shared/CAIconComponent'

/**
	* The `CAHeading` component is used to display a heading with a title and optional action.
	* @component CAHeading
	*
	* Types needed for CAHeading
	* @typedef {(1 | 2 | 3 | 4 | 5 | 6 | 'line' | 'small' | 'x-large')} HeadingLevel - The heading level of the heading. Line is a special one for a line under the heading. Only use if specified.
	* @typedef {('black' | 'blue' | 'gray' | 'white')} HeadingColor - The color of the heading.
	*
	* @prop {string} [additionalClasses=''] - Additional classes to be added to the heading.
	* @prop {string} [additionalDescriptionClasses=''] - Additional classes to be added to the description.
	* @prop {boolean} [alignStart=false] - Whether the title should align to the vertical top or stay as the default, vertical center.
	* @prop {boolean} [centered=false] - Whether the title should be centered.
	* @prop {CAHeadingColor} [color=''] - The color of the heading.
	* @prop {string} [dataTest=''] - The data-test attribute for the component.
	* @prop {string} [description=''] - The description of the heading.
	* @prop {boolean} [disableTitleMutation=true] - Whether the title should control its own capitalization. Use for things like names. If this is not selected, we capitalize the first letter and lowercase the rest.
	* @prop {boolean} [editTitle=false] - Whether the title should be editable. If this is selected, an input will be available that will emit the new title.
	* @prop {string} [headerActionText=''] - The text for the header action link.
	* @prop {CAHeadingLevel} [heading=1] - The heading level of the heading. Line is a special one for a line under the heading. Only use if specified.
	* @prop {string} [icon=''] - The icon for the header.
	* @prop {string} [iconAfterTitle=''] - The icon for the end of the title. Find the icons in the Icons page of the documentation and use their name.
	* @prop {string} [iconBeforeTitle=''] - The icon for the start of the title. Find the icons in the Icons page of the documentation and use their name.
	* @prop {boolean} [loading=false] - Whether the heading is in a loading state.
	* @prop {number} [minCharacterCount=3] - The minimum character count for the title.
	* @prop {string} [modelValue=''] - The model value for the title, used for v-model binding.
	* @prop {boolean} [optimize=true] - Whether to optimize the heading for readability.
	* @prop {string} [overrideColorClass=''] - The color class to override the default heading color.
	* @prop {string} [title=''] - The title of the heading. Use if you can't use v-model.
	* @prop {string} [tooltip=''] - The tooltip over the title of the title.
	* @prop {string} [tooltipAfterTitle=''] - The tooltip after the title.
	* @prop {string} [tooltipBeforeTitle=''] - The tooltip before the title.
	*
	* @slot action - The action slot for the heading component, used to add an action link.
	* @slot default - The default slot for the heading component.
	* @slot titleFlare - The slot option to add a flare to the title, i.e. <CABadge />, icons, etc.
	*
	* @emit click - Emits when the action link is clicked.
	* @emit clickHeadingIcon - Emits when the heading icon is clicked.
	* @emit clickTitle - Emits when the title is clicked.
	* @emit update:modelValue - Emits the new title when the title is edited.
*/

export default defineComponent({
	name: 'CAHeading',

	props: {
		/**
		 * Additional classes to be added to the heading.
		 * @default ''
		 * @type {string}
		 */
		additionalClasses: { default: '', type: String },
		/**
		 * Additional classes to be added to the description.
		 * @default ''
		 * @type {string}
		 */
		additionalDescriptionClasses: { default: '', type: String },
		/**
		 * Whether the title should align to the vertical top or stay as the default, vertical center.
		 * @default false
		 * @type {boolean}
		 */
		alignStart: { default: false, type: Boolean },
		/**
		 * Whether the title should be centered.
		 * @default false
		 * @type {boolean}
		 */
		centered: { default: false, type: Boolean },
		/**
		 * The color of the heading.
		 * @default ''
		 * @type {CAHeadingColor}
		 * @enum {('black' | 'blue' | 'gray' | 'white')}
		 */
		color: { default: '', type: String as PropType<CAHeadingColor> },
		/**
		 * The data-test attribute for the component.
		 * @default ''
		 * @type {string}
		 */
		dataTest: { default: '', type: String },
		/**
		 * The description of the heading.
		 * @default ''
		 * @type {string}
		 */
		description: { default: '', type: String },
		/**
		 * Whether the title should control its own capitalization. Use for things like names. If this is not selected, we capitalize the first letter and lowercase the rest.
		 * @default true
		 * @type {boolean}
		 */
		disableTitleMutation: { default: true, type: Boolean },
		/**
		 * Whether the title should be editable. If this is selected, an input will be available that will emit the new title.
		 * @default false
		 * @type {boolean}
		 */
		editTitle: { default: false, type: Boolean },
		/**
		 * The text for the header action link.
		 * @default ''
		 * @type {string}
		 */
		headerActionText: { default: '', type: String },
		/**
		 * The heading level of the heading. Line is a special one for a line under the heading. Only use if specified.
		 * @default 1
		 * @type {CAHeadingLevel}
		 * @enum {('1' | '2' | '3' | '4' | '5' | '6' | 'line' | 'small' | 'x-large')}
		 */
		heading: {
			default: 1,
			type: [
				Number,
				String,
			] as PropType<CAHeadingLevel>,
		},
		/**
		 * The icon for the header.
		 * @default ''
		 * @type {string}
		 */
		icon: { default: '', type: String },
		/**
		 * The icon for the end of the title. Find the icons in the Icons page of the documentation and use their name.
		 * @default ''
		 * @type {string}
		 */
		iconAfterTitle: { default: '', type: String },
		/**
		 * The icon for the start of the title. Find the icons in the Icons page of the documentation and use their name.
		 * @default ''
		 * @type {string}
		 */
		iconBeforeTitle: { default: '', type: String },
		/**
		 * Whether the heading is in a loading state.
		 * @default false
		 * @type {boolean}
		 */
		loading: { default: false, type: Boolean },
		/**
		 * The minimum character count for the title.
		 * @default 3
		 * @type {number}
		 */
		minCharacterCount: { default: 3, type: Number },
		modelValue: { default: '', type: String },
		/**
		 * Whether to optimize the heading for readability.
		 * @default true
		 * @type {boolean}
		 */
		optimize: { default: true, type: Boolean },
		/**
		 * The color class to override the default heading color.
		 * @default ''
		 * @type {string}
		 */
		overrideColorClass: { default: '', type: String },
		/**
		 * The title of the heading. Use if you can't use v-model.
		 * @default ''
		 * @type {string}
		 */
		title: { default: '', type: String },
		/**
		 * The tooltip over the title of the title.
		 * @default ''
		 * @type {string}
		 */
		tooltip: { default: '', type: String },
		/**
		 * The tooltip after the title.
		 * @default ''
		 * @type {string}
		 */
		tooltipAfterTitle: { default: '', type: String },
		/**
		 * The tooltip before the title.
		 * @default ''
		 * @type {string}
		 */
		tooltipBeforeTitle: { default: '', type: String },
	},

	emits: {
		'update:modelValue': (value: string) => {
			return typeof value === 'string' && value.length >= 0
		},
		click: () => true,
		clickHeadingIcon: () => true,
		clickTitle: () => true,
		titleError: () => true,
	},

	data(): Data {
		return {
			editing: false,
			newTitle: this.modelValue ?? this.title ?? '',
			originalTitle: this.modelValue ?? this.title ?? '',
			titleError: false,
		}
	},

	computed: {
		component(): string {
			if (this.heading === 'x-large' || this.heading === 1) {
				return 'h1'
			}

			if (this.heading === 'small') {
				return 'h6'
			}

			if (this.heading === 'line') {
				return 'h2'
			}

			if (typeof this.heading === 'number' && this.heading >= 2 && this.heading <= 6) { // eslint-disable-line no-magic-numbers
				return `h${this.heading}`
			}

			console.warn('Invalid heading level, defaulting to h1')

			return 'h1'
		},
	},

	watch: {
		modelValue(newValue) {
			if (newValue !== this.newTitle) {
				this.newTitle = newValue
				this.originalTitle = newValue
			}
		},

		title(newTitle) {
			if (newTitle !== this.newTitle) {
				this.newTitle = newTitle
				this.originalTitle = newTitle
			}
		},
	},

	mounted() {
		const title = !!this.title ? this.title : this.modelValue

		this.newTitle = title
		this.originalTitle = title
	},

	methods: {
		createDebounce(func: Function, delay: number = DELAY.HALF_SECOND) {
			let timeoutId: ReturnType<typeof setTimeout>

			return (...args: any[]) => {
				clearTimeout(timeoutId)
				timeoutId = setTimeout(() => func.apply(this, args), delay)
			}
		},

		emitClickTitle() {
			if (!this.loading) {
				this.$emit('clickTitle')
			}
		},

		emitNewTitle(newTitle: string) {
			if (newTitle.length <= this.minCharacterCount) {
				this.titleError = true
				this.$emit('titleError')

				return
			}

			if (newTitle !== this.originalTitle) {
				this.newTitle = newTitle
				this.originalTitle = newTitle
				this.$emit('update:modelValue', newTitle)
			}

			this.editing = false
			this.titleError = false
		},

		handleEnterKey() {
			if (this.titleError || this.newTitle.length <= this.minCharacterCount) {
				this.$emit('titleError')

				return
			}

			if (this.newTitle.length > 0) {
				this.emitNewTitle(this.newTitle)
			}
		},

		handleInput(event: Event) {
			const target = event.target as HTMLInputElement
			const value = target.value

			this.newTitle = value
			this.titleError = value.length <= this.minCharacterCount
		},

		mutateTitle(title: string): string {
			if (this.disableTitleMutation || title === '' || !title) {
				return title
			}

			return title.charAt(0).toUpperCase() + title.slice(1).toLowerCase()
		},

		reset() {
			this.newTitle = this.originalTitle
			this.editing = false
			this.titleError = false
		},
	},

	render() {
		const HeadingTag = this.component

		return (
			<HeadingTag>
				<span
					class={{
						'ca-heading': true,
						'ca-heading--has-action': !!this.headerActionText,
						'ca-heading--has-tooltip': !!this.tooltip || !!this.tooltipBeforeTitle || !!this.tooltipAfterTitle,
						'ca-heading--with-icon': this.icon !== '' || this.iconBeforeTitle !== '' || this.iconAfterTitle !== '',
						'ca-heading--line': this.heading === 'line',
						'ca-heading--small': this.heading === 'small',
						'ca-heading--x-large': this.heading === 'x-large',
						blue: this.color === 'blue' && !this.overrideColorClass,
						gray: this.color === 'gray' && !this.overrideColorClass,
						white: this.color === 'white' && !this.overrideColorClass,
						black: this.color === 'black' && !this.overrideColorClass,
						[this.overrideColorClass || '']: !!this.overrideColorClass,
						[this.additionalClasses || '']: !!this.additionalClasses,
					}}
					data-test={this.dataTest}
					data-test-component="ca-header"
				>
					<span
						class={{
							'ca-heading__ignore-span flex justify-between w-full': true,
							'flex-col md:flex-row': this.optimize,
							'items-start': this.alignStart,
							'items-center': !this.alignStart,
						}}
					>
						<span
							class={[
								'ca-heading__ignore-span inline-flex gap-2 items-center w-full',
								{ 'justify-center': this.centered },
							]}
						>
							{this.iconBeforeTitle &&
								<em
									data-test="ca-heading-icon-before-title"
									v-tooltip={this.tooltipBeforeTitle}
									onClick={() => this.$emit('clickHeadingIcon')}
								>
									<CAIconComponent class="ca-heading--action__icon" icon={this.iconBeforeTitle} />
								</em>
							}

							{!this.editing && !this.loading ?
								<span
									class={{
										'ca-heading__title': true,
										'disable-title-mutation': this.disableTitleMutation,
										'ca-heading__title--small': this.heading === 'small',
										[this.overrideColorClass || '']: !!this.overrideColorClass,
									}}
									data-test="click-title"
									role="heading"
									v-tooltip={this.tooltip}
									onClick={this.emitClickTitle}
								>
									{this.newTitle ?
										<span>{this.mutateTitle(this.newTitle)}</span>

										:

										this.$slots.default?.()
									}
								</span>

								:

								this.loading ?
									<span>
										<CAIconComponent
											class="text-blue"
											icon="SVGLoading"
											svg-class="w-10 h-10"
										/>
									</span>

									:

									this.editing && this.editTitle && !this.loading ?
										<div class="ca-heading__editing-input-wrapper">
											<input
												class="ca-heading__editing-input"
												data-test="ca-heading-editing-input"
												value={this.newTitle}
												onInput={this.handleInput}
												onKeyup={e => e.key === 'Enter' && this.handleEnterKey()}
											/>

											{this.editing &&
												<span class="flex">
													<span onClick={this.reset}>
														<CAIconComponent
															class="inline-block w-auto text-gray-500 cursor-pointer"
															data-test="ca-heading-reset"
															icon="SVGCircleXFilled"
															svg-class="w-6 h-6"
														/>
													</span>

													<span class="px-0" onClick={() => this.emitNewTitle(this.newTitle)}>
														<CAIconComponent
															class={[
																'inline-block w-auto',
																{ 'text-blue-300 cursor-pointer': !this.titleError },
																{ 'text-gray-200 cursor-not-allowed': this.titleError },
															]}
															data-test="ca-heading-update-checkmark"
															icon="SVGCheckCircleFilled"
															svg-class="w-6 h-6"
														/>
													</span>
												</span>
											}
										</div>

										:

										null
							}

							{!this.editing && this.editTitle && !this.loading &&
								<span onClick={() => {
									this.editing = true
									this.titleError = false
								}}>
									<CAIconComponent
										class="cursor-pointer text-blue-300 hover:text-blue"
										data-test="ca-heading-edit-button"
										icon="SVGEdit"
									/>
								</span>
							}

							{this.iconAfterTitle &&
								<em
									data-test="ca-heading-icon-after-title"
									v-tooltip={this.tooltipAfterTitle}
									onClick={() => this.$emit('clickHeadingIcon')}
								>
									<CAIconComponent class="ca-heading--action__icon" icon={this.iconAfterTitle} />
								</em>
							}

							{this.$slots.titleFlare?.()}
						</span>

						{this.$slots.action?.() &&
							<div class="ca-heading--action">{this.$slots.action?.()}</div>
						}

						{this.headerActionText &&
							<a
								class="ca-heading--action"
								data-test="ca-heading-action"
								onClick={() => this.$emit('click')}
							>
								{this.icon !== '' &&
									<CAIconComponent class="ca-heading--action__icon" icon={this.icon} />
								}

								<span>{this.headerActionText}</span>
							</a>
						}
					</span>

					{this.titleError &&
						<div class="ca-heading__error">
							<CAIconComponent icon="SVGAlertShield" />
							<span>Title must be more than {this.minCharacterCount.toString()} characters.</span>
						</div>
					}

					{this.description &&
						<p
							class={[
								'ca-heading__description',
								{
									'mx-auto text-center': this.centered,
									[this.additionalDescriptionClasses]: !!this.additionalDescriptionClasses,
								},
							]}

						>
							{this.description}
						</p>
					}
				</span>
			</HeadingTag>
		)
	},
})

interface Data {
	editing: boolean
	newTitle: string
	originalTitle: string
	titleError: boolean
}
